import React, { useState, useEffect } from 'react';
import { Lightbulb, LockOpen, PartyPopper, TriangleAlert } from 'lucide-react';
import { selectiveApi } from '../../services/selectiveApi';
import './SessionResults.css';

/**
 * SessionResults Component
 *
 * Displays comprehensive results after completing a practice/mock exam session.
 * Shows overall score, section breakdown, domain analytics, tier unlocks, and recommendations.
 */
const SessionResults = ({ sessionId, initialResults = null, onStartNewSession, onReviewAnswers, onBackToDashboard }) => {
  // initialResults comes from the /complete call and includes tier_unlocks;
  // when absent (e.g. revisiting), results are fetched from /results.
  const [results, setResults] = useState(initialResults);
  const [loading, setLoading] = useState(!initialResults);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, breakdown, review

  useEffect(() => {
    if (!initialResults) {
      fetchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchResults = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await selectiveApi.sessions.getResults(sessionId);
      setResults(data);
    } catch (err) {
      console.error('[SessionResults] Error fetching session results:', err);
      setError(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="session-results loading">
        <div className="loading-spinner"></div>
        <p>Calculating your results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="session-results error">
        <div className="error-message">
          <h3><TriangleAlert size={20} className="inline-icon" /> Error Loading Results</h3>
          <p>{error}</p>
          <button onClick={fetchResults} className="retry-button">Try Again</button>
        </div>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  // Map API response to component variables
  const {
    session_id,
    percentage,
    max_score,
    overall_score,
    time_spent_seconds,
    breakdown_by_skill = [],
    question_review = [],
    strongest_skills = [],
    weakest_skills = [],
    recommendations = []
  } = results;

  // Calculate derived values for display
  const score_percentage = percentage;
  const total_questions = max_score;
  const correct_answers = overall_score;

  // Count incorrect answers from question_review (not all unanswered!)
  const incorrect_answers = question_review.filter(q => !q.is_correct).length;
  const unanswered = total_questions - question_review.length;

  const time_spent_minutes = Math.round(time_spent_seconds / 60);
  const section_breakdown = breakdown_by_skill;
  const domain_breakdown = breakdown_by_skill;
  const tier_unlocks = results.tier_unlocks || [];

  const getScoreGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', label: 'Outstanding', color: '#22c55e' };
    if (percentage >= 80) return { grade: 'A', label: 'Excellent', color: '#10b981' };
    if (percentage >= 70) return { grade: 'B', label: 'Good', color: '#3b82f6' };
    if (percentage >= 60) return { grade: 'C', label: 'Satisfactory', color: '#eab308' };
    if (percentage >= 50) return { grade: 'D', label: 'Pass', color: '#f97316' };
    return { grade: 'F', label: 'Needs Improvement', color: '#ef4444' };
  };

  const scoreGrade = getScoreGrade(score_percentage);

  return (
    <div className="session-results">

      {/* Header with Overall Score */}
      <div className="results-header">
        <div className="score-circle" style={{ borderColor: scoreGrade.color }}>
          <div className="score-percentage" style={{ color: scoreGrade.color }}>
            {score_percentage}%
          </div>
          <div className="score-grade" style={{ color: scoreGrade.color }}>
            {scoreGrade.grade}
          </div>
        </div>
        <div className="header-info">
          <h2>Session Complete!</h2>
          <p className="score-label" style={{ color: scoreGrade.color }}>
            {scoreGrade.label}
          </p>
          <div className="quick-stats">
            <div className="stat">
              <span className="stat-value">{correct_answers}</span>
              <span className="stat-label">Correct</span>
            </div>
            <div className="stat">
              <span className="stat-value">{incorrect_answers}</span>
              <span className="stat-label">Incorrect</span>
            </div>
            {unanswered > 0 && (
              <div className="stat">
                <span className="stat-value">{unanswered}</span>
                <span className="stat-label">Unanswered</span>
              </div>
            )}
            <div className="stat">
              <span className="stat-value">{total_questions}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat">
              <span className="stat-value">{time_spent_minutes}m</span>
              <span className="stat-label">Time Taken</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Unlocks Banner */}
      {tier_unlocks && tier_unlocks.length > 0 && (
        <div className="unlocks-banner">
          <h3><PartyPopper size={20} className="inline-icon" /> New Tiers Unlocked!</h3>
          <div className="unlocks-list">
            {tier_unlocks.map((unlock, index) => (
              <div key={index} className="unlock-item">
                <span className="unlock-icon"><LockOpen size={18} /></span>
                <span className="unlock-domain">{unlock.domain_name}</span>
                <span className="unlock-tier">{unlock.tier}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="results-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'breakdown' ? 'active' : ''}`}
          onClick={() => setActiveTab('breakdown')}
        >
          Detailed Breakdown
        </button>
        <button
          className={`tab ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Review Answers
        </button>
      </div>

      {/* Tab Content */}
      <div className="results-content">

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">

            {/* Section Performance */}
            {section_breakdown && section_breakdown.length > 0 && (
              <div className="results-card">
                <h3>Performance by Skill</h3>
                <div className="section-bars">
                  {section_breakdown.map((skill, index) => (
                    <div key={index} className="section-item">
                      <div className="section-header">
                        <span className="section-name">{skill.skill_name}</span>
                        <span className="section-score">{skill.percentage}%</span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${skill.percentage}%`,
                            backgroundColor: getProgressColor(skill.percentage)
                          }}
                        />
                      </div>
                      <div className="section-stats">
                        {skill.correct} correct • {skill.total} questions
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {recommendations && recommendations.length > 0 && (
              <div className="results-card">
                <h3>Recommended Next Steps</h3>
                <div className="recommendations-list">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <div className="rec-icon"><Lightbulb size={24} /></div>
                      <div className="rec-content">
                        <div className="rec-reason">{rec}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Time Analysis */}
            <div className="results-card">
              <h3>Time Analysis</h3>
              <div className="time-stats">
                <div className="time-stat">
                  <span className="time-label">Time Spent</span>
                  <span className="time-value">{time_spent_minutes} minutes</span>
                </div>
                <div className="time-stat">
                  <span className="time-label">Avg. per Question</span>
                  <span className="time-value">
                    {Math.round(time_spent_seconds / total_questions)}s
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Breakdown Tab */}
        {activeTab === 'breakdown' && (
          <div className="breakdown-tab">
            <div className="results-card">
              <h3>Performance by Skill</h3>
              {domain_breakdown && domain_breakdown.length > 0 ? (
                <div className="domain-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Skill</th>
                        <th>Questions</th>
                        <th>Correct</th>
                        <th>Score</th>
                        <th>Avg Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {domain_breakdown.map((skill, index) => (
                        <tr key={index}>
                          <td className="domain-name">{skill.skill_name}</td>
                          <td>{skill.total}</td>
                          <td>{skill.correct}</td>
                          <td>
                            <span className={`score-badge ${getScoreClass(skill.percentage)}`}>
                              {skill.percentage}%
                            </span>
                          </td>
                          <td>{Math.round(skill.avg_time_seconds)}s</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="empty-state">No skill breakdown available</p>
              )}
            </div>
          </div>
        )}

        {/* Review Tab */}
        {activeTab === 'review' && (
          <div className="review-tab">
            <div className="results-card">
              <h3>Question-by-Question Review</h3>
              {question_review && question_review.length > 0 ? (
                <div className="question-review-list">
                  {question_review.map((q, index) => (
                    <div key={q.question_id} className={`review-question ${q.is_correct ? 'correct' : 'incorrect'}`}>
                      <div className="review-question-header">
                        <span className="question-number">Question {index + 1}</span>
                        <span className={`result-badge ${q.is_correct ? 'correct' : 'incorrect'}`}>
                          {q.is_correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                        <span className="time-badge">{q.time_taken_seconds}s</span>
                      </div>
                      <div className="review-question-text">{q.question_text}</div>

                      {/* Show all options */}
                      {q.options && q.options.length > 0 && (
                        <div className="review-options">
                          {q.options.map((opt) => {
                            const isUserAnswer = opt.option_letter === q.user_answer;
                            const isCorrectAnswer = opt.option_letter === q.correct_answer;
                            let optionClass = 'review-option';
                            if (isCorrectAnswer) optionClass += ' correct-option';
                            if (isUserAnswer && !q.is_correct) optionClass += ' incorrect-option';

                            return (
                              <div key={opt.id} className={optionClass}>
                                <span className="option-letter">{opt.option_letter}</span>
                                <span className="option-text">{opt.option_text}</span>
                                {isUserAnswer && <span className="badge">Your Answer</span>}
                                {isCorrectAnswer && <span className="badge correct">Correct</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {q.explanation && q.explanation !== 'No explanation available' && (
                        <div className="review-explanation">
                          <strong>Explanation:</strong>
                          <p>{q.explanation}</p>
                        </div>
                      )}
                      <div className="review-meta">
                        <span className="skill-tag">{q.skill_code}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No questions to review</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="results-actions">
        <button onClick={onBackToDashboard} className="action-button secondary">
          Back to Dashboard
        </button>
        <button onClick={onStartNewSession} className="action-button primary">
          Start New Session
        </button>
      </div>
    </div>
  );
};

// Helper functions
const getProgressColor = (percentage) => {
  if (percentage >= 80) return '#22c55e';
  if (percentage >= 60) return '#3b82f6';
  if (percentage >= 40) return '#eab308';
  return '#ef4444';
};

const getScoreClass = (score) => {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
};

export default SessionResults;
